import {
  Component,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Signal,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import * as am5 from "@amcharts/amcharts5";

import {TramRegistroService} from '../../../services/database/tramRegistro.service';
import {
  IGetDashboardNumeroTramitesPorFecha,
  IGetDashboardTotalTramies
} from '../../../shared/interfaces/database/tramite';
import {CustomTitle} from 'lib-base';
import {CommonModule} from '@angular/common';
import {TranslateService} from '@ngx-translate/core';
import {firstValueFrom, forkJoin} from 'rxjs';
import {SkeletonModule} from 'primeng/skeleton';
import {CalendarModule} from "primeng/calendar";
import {FormsModule} from "@angular/forms";
import {MessageService} from "primeng/api";
import {FilterDashboardComponent} from "../filter-dashboard/filter-dashboard.component";
import {FilterDatesEvent} from "../../../shared/interfaces/database/configuracion";
import {TarjetaKpiComponent, TarjetaKpiData} from "../components/tarjeta-kpi/tarjeta-kpi.component";
import {convertToMillions} from "../dashboard-utils";
import {ChartLineCardConfig, ChartLineComponent} from "../components/chart-line/chart-line.component";
import {HEX_COLOR_PALETTE} from "../components/utils/color-palette";
import {FilterDashboardService} from "../filter-dashboard/filter-dashboard.service";
import dayjs from "dayjs";

@Component({
  selector: 'app-historico-de-tramites',
  standalone: true,
  imports: [CommonModule, CustomTitle, SkeletonModule,
    CalendarModule, FormsModule, FilterDashboardComponent, TarjetaKpiComponent, ChartLineComponent],
  templateUrl: './historico-de-tramites.component.html',
  styleUrls: ['./historico-de-tramites.component.scss', '../../../../assets/styles/style.mantenimiento.scss']
})
export class HistoricoDeTramitesComponent implements OnInit, OnDestroy {

  readonly  colors = HEX_COLOR_PALETTE;
  totalTramites: WritableSignal<TarjetaKpiData[]> = signal([]);
  isLoadingTotalTramite: boolean = false;
  isLoadingTramiesPorFecha: boolean = false;
  lineConfig!: ChartLineCardConfig;
  translations: any = {};

  @ViewChild(ChartLineComponent) chartLineComponent!: ChartLineComponent;

  constructor(private _tr: TramRegistroService, private filterDateService:FilterDashboardService,
              private _t: TranslateService, public messageService: MessageService) {
    this.initChartLine();
  }

  ngOnInit() {
    this.filterDateService.setSelectedDates( dayjs().startOf('year').toDate(), new Date())
    this.translate().then(() => {
      this.initTotalsTramites()
      this.initChartLine();
    });
  }

  ngOnDestroy(): void {

  }

  private async translate(): Promise<void> {
    this.translations = await firstValueFrom(forkJoin({
      dashboard: this._t.get('common.dashboard'),
      recibidos: this._t.get('components.app-historico-de-tramites.recibidos'),
      finalizadosATiempo: this._t.get('components.app-historico-de-tramites.finalizados-a-tiempo'),
      finalizadosFueraPlazo: this._t.get('components.app-historico-de-tramites.finalizados-fuera-plazo'),
      pendientes: this._t.get('components.app-historico-de-tramites.pendientes'),
      efectividad: this._t.get('components.app-historico-de-tramites.efectividad'),
      historico: this._t.get('components.app-historico-de-tramites.historico'),
      filter: this._t.get('components.app-historico-de-tramites.filtros'),
    }));
  }


  private initChartLine() {
    this.lineConfig = {
      title: this.translations.historico,
      chart: {
        chartId: 'historico-line',
        categoryField: 'fechaSolicitud',
        xLabelRotation:45,
        series: [
          {field: 'value', name: 'Trámites Realizados', color: this.colors[0], dashed: false},
        ],
        data: [],
      },
    };
  }


  //#region chart Historico Tramite

  chartTramitesPorFecha(data: any[]) {
    if (!this.chartLineComponent) return;
    this.chartLineComponent.load(data);

  }

  getDashboardNumeroTramitesPorFecha(startDate: Date, endDate: Date, granularity: 'day' | 'week' | 'month') {
    this.isLoadingTramiesPorFecha = true;
    this._tr.getDashboardNumeroTramitesPorFecha(this.castToString(startDate), this.castToString(endDate), granularity).subscribe({
      next: (response: IGetDashboardNumeroTramitesPorFecha[]) => {
        this.chartTramitesPorFecha(response);
      }, complete: () => {
        this.isLoadingTramiesPorFecha = false;
      }
    });
  }

  //#endregion chart Historico Tramite

  changeDates(event: FilterDatesEvent) {
    this.getDashboardTotalTramites(event.startDate, event.endDate);
    this.getDashboardNumeroTramitesPorFecha(event.startDate, event.endDate, event.granularity);
  }

  private initTotalsTramites() {
    this.totalTramites().push(
      {
        label: this.translations.recibidos,
        variant: 'g',
        value: '0'
      },
      {
        label: this.translations.finalizadosATiempo,
        variant: 'c',
        value: '0'
      },
      {
        label: this.translations.finalizadosFueraPlazo,
        value: '0',
        variant: 'r'
      },
      {
        label: this.translations.pendientes,
        value: '0',
        variant: 'a'
      },
      {

        label: this.translations.efectividad,
        value: '0%',
        variant: 'p'
      });

  }

  private updateTotals(response: IGetDashboardTotalTramies) {
    this.totalTramites.update(prev => {
      const array = [...prev];
      array[0].value = `${convertToMillions(response.tramitesRecibidos ?? 0, 0)}`;
      array[1].value = `${convertToMillions(response.tramitesFinalizadosATiempo ?? 0, 0)}`;
      array[2].value = `${convertToMillions(response.tramitesFinalizadosFueraDePlazo ?? 0, 0)}`;
      array[3].value = `${convertToMillions(response.tramitesPendientes ?? 0, 0)}`;
      array[4].value = `${(response.efectividad*100).toFixed(2)}%`;
      return array;
    });
  }

  //#region API Calls
  getDashboardTotalTramites(firstDate: Date, lastDate: Date) {
    this.isLoadingTotalTramite = true;
    const startDate = this.castToString(firstDate);
    const endDate = this.castToString(lastDate);
    this._tr.getDashboardTotalTramites(startDate, endDate).subscribe({
      next: (response: IGetDashboardTotalTramies) => {
        this.updateTotals(response)
      }, complete: () => {
        this.isLoadingTotalTramite = false;
      }
    });
  }

  //#endregion

  private castToString(date: Date): string {
    if (!date) throw 'Invalid date';
    return date.toISOString().substring(0, date.toISOString().indexOf('T'));
  }
}
